// UnitPricingProposal.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import HeadingCp from "../components/HeadingCp";
import EstimateBar from "../components/EstimateBar";
import CurrencyFormat from "../components/CurrencyFormat";
import DateFormat from "../components/DateFormat";
import { toast } from "react-toastify";

const toNum = (v) => {
  const n = typeof v === "string" ? v.replace(/[, ]+/g, "") : v;
  const f = Number(n);
  return Number.isFinite(f) ? f : 0;
};

function numberToWordsUSD(n) {
  if (n == null || isNaN(n)) return "";
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const words = (num) => {
    if (num < 20) return ones[num];
    if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? " " + ones[num%10] : "");
    if (num < 1000) return ones[Math.floor(num/100)] + " Hundred" + (num%100 ? " " + words(num%100) : "");
    if (num < 1_000_000) return words(Math.floor(num/1000)) + " Thousand" + (num%1000 ? " " + words(num%1000) : "");
    if (num < 1_000_000_000) return words(Math.floor(num/1_000_000)) + " Million" + (num%1_000_000 ? " " + words(num%1_000_000) : "");
    return "";
  };
  const [dollars, cents] = parseFloat(n).toFixed(2).split(".");
  let result = words(parseInt(dollars, 10)) + " Dollar" + (parseInt(dollars, 10) === 1 ? "" : "s");
  if (parseInt(cents, 10) > 0) {
    result += " and " + words(parseInt(cents, 10)) + " Cent" + (parseInt(cents, 10) === 1 ? "" : "s");
  }
  return result.trim();
}

export default function UnitPricingProposal() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { id } = useParams();               // estimate id
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { header, items }

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiUrl}/unit_pricing_crud.php?estimate_detail=${id}`, {
        withCredentials: true,
      });
      if (!data?.header) {
        toast.error("Proposal not found", { position: "top-center" });
        // navigate(-1);
        return;
      }
      setDetail(data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
      else toast.error("Failed to load proposal", { position: "top-center" });
    } finally {
      setLoading(false);
    }
  }, [apiUrl, id, navigate]);

  useEffect(() => {
    if (id) fetchDetail();
  }, [id, fetchDetail]);

  if (loading) return <div className="p-10 text-center">Loading Proposal...</div>;
  if (!detail) return <div className="p-10 text-center text-red-600">Proposal not found.</div>;

  const h = detail.header || {};
  const items = Array.isArray(detail.items) ? detail.items : [];

  const projectDate = h.date || new Date().toISOString().slice(0, 10);
  const finalPrice = toNum(h.total);
  const subTotal = toNum(h.subtotal);
  const taxRate = toNum(h.tax_rate);
  const tax = toNum(h.tax);

  const renderAddressBlock = () => (
    <>
      <div className="font-bold">{h.company || ""}</div>
      <div>{h.contact_name || ""}</div>
      <div>{h.company_address || ""}</div>
    </>
  );

  const renderEquipment = () => (
    <ol className="mb-2 pl-4 list-decimal">
      {items.map((eq, idx) => {
        const qty = toNum(eq.quantity);
        return (
          <li key={idx}>
            <b>({qty})</b> {eq.device}
            <span className="text-gray-500"> {eq.description ? ` — ${eq.description}` : ""}</span>
          </li>
        );
      })}
      <li>Supply all Programming for correct operation.</li>
      <li>Generate Shop Drawings and Submittals.</li>
      <li>Provide NICET Level IV Project Management.</li>
      <li>Test/Demo with Building Personnel.</li>
    </ol>
  );

  const handleDownloadServerPdf = async () => {
    // Point this to your actual unit-pricing PDF generator
    const url = `${apiUrl}/gen_pdf_unit_pricing.php?estimate_detail=${id}`;
    try {
      const resp = await axios.get(url, { responseType: "blob", withCredentials: true });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = `UnitPricing_Proposal_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(pdfUrl);
    } catch (err) {
      console.error("Download PDF error:", err);
      toast.error("Unable to download PDF", { position: "top-center" });
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] sm:px-6 lg:px-6 py-8 bg-white">
      <a href="/unit_pricing_estimate_list" className="text-sm font-semibold leading-6 text-blue-700">
        <span aria-hidden="true">&larr;</span> Back to Estimate List
      </a>
      <div
        style={{ fontFamily: "Cambria, Cochin, Georgia, Times, 'Times New Roman', serif" }}
        className="mx-auto max-w-[900px] bg-white p-8 rounded shadow"
      >
        <HeadingCp label="Proposal" />

        <div className="no-print mb-4">
          <button
            onClick={handleDownloadServerPdf}
            className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          >
            Download PDF
          </button>
        </div>

        <div className="mb-2 text-sm">
          <div><DateFormat date={projectDate} /></div>
          <br />
          <div className="text-base">{renderAddressBlock()}</div>
        </div>

        <div className="my-8 font-semibold text-lg text-center underline">
          Re: Proposal for Fire Alarm Equipment Supply
        </div>

        <div className="mb-4 text-base font-extrabold">Dear {h.contact_name || "Sir/Madam"},</div>

        <div className="mb-6">
          We are pleased to present our proposal for the supply of your Fire Alarm System components. Our team is
          committed to providing quality equipment and support to ensure a successful project.
        </div>

        <div className="mb-3">
          <div className="mb-4">
            Empire Fire Alarm Specialist Co., Inc. shall provide the following equipment and services:
          </div>
          <div className="ml-10">{renderEquipment()}</div>
        </div>

        <div className="my-10">
          <div className="text-lg font-semibold underline">PRICE:</div>
          <br />
          <div className="width-full max-w-lg border p-4 rounded">
            <div className="flex justify-between">
              <span className="text-gray-700">Subtotal</span>
              <span className="font-semibold"><CurrencyFormat value={subTotal} /></span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Tax ({taxRate.toFixed(3)}%)</span>
              <span className="font-semibold"><CurrencyFormat value={tax} /></span>
            </div>
            <div className="flex justify-between text-xl pt-2 border-t mt-2">
              <span className="font-bold">Total</span>
              <span className="font-extrabold"><CurrencyFormat value={finalPrice} /></span>
            </div>
          </div>
          {/* <div className="mt-3 font-extrabold">
            {finalPrice ? `(${numberToWordsUSD(finalPrice)}).` : ""}
          </div> */}
          {/* <div className="text-sm italic mt-1">
            Kindly note that the quoted price excludes any applicable sales tax unless specified above.
          </div> */}
        </div>

        {h.notes && (
          <div className="my-8">
            <div className="font-semibold underline text-lg mb-2">NOTES & QUALIFICATIONS:</div>
            <div className="ml-2 whitespace-pre-wrap">{h.notes}</div>
          </div>
        )}

        <div className="mt-10">
          Thank you for considering Empire Fire Alarm Specialist Co., Inc. as your trusted partner. We appreciate the
          opportunity to submit this proposal and are available for any questions or clarifications.
          <br />
          <br />
          Sincerely,
          <br />
          {/* <b>{h.sales_rep_name || 'Empire Fire Alarm Specialist Co., Inc.'}</b> */}
        </div>
      </div>
    </div>
  );
}
